// eslint-disable-next-line max-classes-per-file

// eslint-disable-next-line max-classes-per-file
type SignalCallback<T> = (arg: T) => unknown;
type SignalFilter<T> = (arg: T) => boolean;

export class SignalBinding<T> {
  public active = true;

  private readonly once: boolean = false;

  private callback: ((arg: T) => void) | null;

  private filter: ((arg: T) => boolean) | null;

  private signal: Signal<T> | null;

  constructor(
    signal: Signal<T>,
    callback: (arg: T) => unknown,
    filter: ((arg: T) => boolean) | null = null,
    once = false,
  ) {
    this.signal = signal;
    this.callback = callback;
    this.filter = filter;
    this.once = once;
  }

  public cleanOn(signal: Signal<unknown>): this {
    signal.addOnce(() => {
      this.destroy();
    });
    return this;
  }

  public execute(arg: T): void {
    if (!this.signal || !this.callback || !this.active) return;
    if (this.filter && !this.filter(arg)) return;
    const cb = this.callback;
    cb(arg);

    if (this.once) this.destroy();
  }

  public async executeAsync(arg: T): Promise<void> {
    if (!this.signal || !this.callback || !this.active) return;
    if (this.filter && !this.filter(arg)) return;
    const cb = this.callback;
    await (cb as (arg: T) => Promise<void>)(arg);

    if (this.once) this.destroy();
  }

  public destroy(): void {
    this.signal?.remove(this);
    this.signal = null;
    this.callback = null;
    this.filter = null;
  }

  public isDestroyed(): boolean {
    return this.signal === null;
  }
}

export class Signal<T = void> {
  public active = true;

  public memorize = false;

  private readonly bindings: SignalBinding<T>[] = [];

  private readonly prevParameters: T[] = [];

  public pass(otherSignal: Signal<T>): SignalBinding<T> {
    return this.add((arg: T) => {
      otherSignal.dispatch(arg);
    });
  }

  public wait(filter?: SignalFilter<T>): Promise<T> {
    return new Promise((resolve: (arg: T) => void): void => {
      this.addOnce(resolve, filter);
    });
  }

  public dispatch(arg: T): void {
    if (!this.active) return;
    if (this.memorize) this.prevParameters.push(arg);
    this.bindings.slice().forEach((b) => {
      b.execute(arg);
    });
  }

  public async pendingDispatch(arg: T): Promise<void> {
    if (!this.active) return;
    if (this.memorize) this.prevParameters.push(arg);
    await Promise.all(this.bindings.slice().map((b) => b.executeAsync(arg)));
  }

  public add(callback: SignalCallback<T>, filter?: SignalFilter<T>): SignalBinding<T> {
    return this.addBinding(new SignalBinding(this, callback, filter, false));
  }

  public addOnce(callback: SignalCallback<T>, filter?: SignalFilter<T>): SignalBinding<T> {
    return this.addBinding(new SignalBinding(this, callback, filter, true));
  }

  private addBinding(binding: SignalBinding<T>): SignalBinding<T> {
    this.bindings.push(binding);
    if (this.memorize)
      this.prevParameters.forEach((arg) => {
        binding.execute(arg);
      });
    return binding;
  }

  public remove(binding: SignalBinding<T>): void {
    if (!this.bindings.includes(binding)) return;
    const index = this.bindings.indexOf(binding);
    if (index !== -1) {
      this.bindings.splice(index, 1);
    }
    binding.destroy();
  }

  public removeAll(): void {
    this.bindings.slice().forEach((binding) => {
      binding.destroy();
    });
    this.bindings.length = 0;
  }
}
